/**
 * The picture for a service row.
 *
 * A services list of six identical text rows is hard to scan, and the vendor had already
 * uploaded photos that nothing ever showed — the gallery editor lived behind the Edit
 * panel and the list rendered none of it.
 *
 * When there is no photo the tile does NOT go blank or grey. It falls back to the module's
 * own icon and tint, so a walk still reads as a walk and a grooming slot as a grooming
 * slot. That is the "module wise" half: the fallback carries meaning rather than
 * apologising for a missing upload.
 */
import { AuthImage } from '@/components/auth-image';
import { module_icon, module_tint } from '@/features/discovery/module-icon';

export function ServiceThumb({
  module_type,
  thumb_path,
  size = 'md',
  className = '',
}: {
  module_type: string;
  /** First image on the service, if it has one. */
  thumb_path?: string | null;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const box = size === 'sm' ? 'h-10 w-10' : 'h-14 w-14';
  const glyph = size === 'sm' ? 'h-4 w-4' : 'h-6 w-6';
  const Icon = module_icon(module_type);

  if (thumb_path) {
    return (
      <div
        className={`${box} shrink-0 overflow-hidden rounded-input bg-muted ${className}`}
        data-testid="service-thumb"
        data-thumb="photo"
      >
        <AuthImage path={thumb_path} alt="" className="h-full w-full object-cover" />
      </div>
    );
  }

  return (
    <div
      className={`${box} flex shrink-0 items-center justify-center rounded-input ${module_tint(module_type)} ${className}`}
      data-testid="service-thumb"
      data-thumb="module"
      /* The module name is the alt text a screen reader needs; the icon alone says nothing. */
      role="img"
      aria-label={module_type.toLowerCase()}
    >
      <Icon className={glyph} />
    </div>
  );
}
